import React, { useEffect } from "react";
import { CircularProgress, Typography, Box } from "@mui/material";
import AuthenticationService from "../AuthenticationService";
import { setActivePage, setAuthentication, setLayout } from "context";
import { useMaterialUIController } from "context";
import { useNavigate } from "react-router-dom";
import CryptoJS from "crypto-js";

const EmbeddedLogin = () => {
  const [controller, dispatch] = useMaterialUIController();
  const { activePage } = controller;
  const navigate = useNavigate();

  const isRole = ["ROLE_ACD_SUPERVISOR"];
  const isDispatcherRole = ["ROLE_RAILWAY_DISPATCHER", "ROLE_MCX_DISPATCHER"];

  const hasDispatcherRole = (userRoles = []) => {
    return userRoles.some((role) => isDispatcherRole.includes(role));
  };

  const hasAllowedRole = (userRoles = []) => {
    return userRoles.some((role) => isRole.includes(role));
  };

  const encryptData = (data, secretKey) => {
    const iv = CryptoJS.lib.WordArray.random(16);
    const ciphertext = CryptoJS.AES.encrypt(
      data,
      CryptoJS.enc.Utf8.parse(secretKey),
      {
        iv: iv,
        mode: CryptoJS.mode.CFB,
        padding: CryptoJS.pad.Pkcs7,
      },
    );

    return iv.concat(ciphertext.ciphertext).toString(CryptoJS.enc.Base64);
  };

  useEffect(() => {
    const handleLoginResponse = (data) => {
      if (!data.token || !data.refreshToken) {
        console.error("Invalid login response:", data);
        return;
      }

      AuthenticationService.storeAuthenticationDetails(
        data.sipPhoneButton,
        data.token,
        data.refreshToken,
        data.userName,
        data.fullName,
        data.roles,
        data.menu,
        data.coralApplication,
        {
          extensionName: data.extensionName,
          extension: data.extension,
          sipPassword: data.sipPassword,
        },
        data.rluCode,
        data.rluIp,
        data.pwdComplication,
        data.userId,
        data.ringAnswerCount,
        data.publicEmergencyNumber,
        data.broadcastNumber,
        data.wssDomain,
        data.mapUrl,
        data.mapCenter,
        data.mapDefaultZoom,
        data.mapMinZoom,
        data.mapMaxZoom,
        data.xmPPPrimaryDomain,
        data.departmentList,
        data.idealTimeout,
        data.description,
        data.appMode,
        data.enableTTS,
        data.disableContextBasedGui,
        data.turnServerUrl,
        data.turnUserName,
        data.turnPassword,
        {
          currencySymbol:      data?.currencySymbol,
          decimalSeparator:    data?.decimalSeparator,
          groupingSeparator:   data?.groupingSeparator,
          groupingStyle:       data?.groupingStyle,
          taxLabel:            data?.taxLabel,
          taxRounding:         data?.taxRounding,
          roundingMode:        data?.roundingMode,
          amountInWords:       data?.amountInWords,
          currencyWord:        data?.currencyWord,
          currencyFractionWord: data?.currencyFractionWord,
        },
      );

      const userRoles = data?.roles || [];

      window.sessionStorage.setItem(
        `${window.location.hostname}-coral-X-User`,
        encryptData(data.username, window.secretKey),
      );

      if (hasDispatcherRole(userRoles)) {
        window.location.href = "/dispatcher";
        return;
      }

       if (hasAllowedRole(userRoles)) {
         window.location.href = "/home-page";
         return;
       }

      activePage.key = "sign.in";
      activePage.authenticationStatus = true;
      setActivePage(dispatch, activePage);
      setAuthentication(dispatch, true);
      setLayout(dispatch, "dashboard");

     
      navigate("/app/home", { replace: true });
    };

    const handleMessage = (event) => {
      if (event?.data?.eventType === "loginResponse") {
        console.log("Login response received:", event.data);
        handleLoginResponse(event.data.data);
      } else {
        console.error("Unexpected event data:", event.data);
      }
    };

    window.addEventListener("message", handleMessage);

    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, [activePage, dispatch, navigate]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        height: "100vh",
      }}
    >
      <CircularProgress size={60} thickness={4} color="info" />
      <Typography
        variant="h6"
        sx={{ marginTop: 2, color: "text", fontSize: "25px" }}
      >
        Please wait, we are authenticating you...
      </Typography>
    </Box>
  );
};

export default EmbeddedLogin;
